import type { Placement, Owner } from './types';

/**
 * Per the rules doc:
 *
 *   "We split the placement WGP evenly based on what's in the field.
 *    If 3 are filled the WGP is split 1/3rd each time the person appears.
 *    If 4 are filled the WGP is split 1/4th each time the person appears."
 *
 * So the split for a given OID on a placement is:
 *
 *   WGP × (appearances ÷ filled-count)
 *
 * Who counts as "appearing" depends on the Selection:
 *
 * - `direct` members (Contract-team consultants, or the single person being
 *   viewed) are credited whenever they hold a role.
 * - `routed` members (perm-desk users from Bristol / London / Chicago) are
 *   credited only when the deal's own currency is in `routedCurrencies` —
 *   GBP deals route to the London total, USD deals to Chicago, anything
 *   else is uncredited (per Jason, 2026-07-03).
 */
export interface Selection {
  direct: ReadonlySet<string>;
  routed: ReadonlySet<string>;
  routedCurrencies: ReadonlySet<string>;
}

const EMPTY_SET: ReadonlySet<string> = new Set();

/** A Selection with no currency routing — self view or a single person. */
export function selectionForOids(oids: ReadonlySet<string>): Selection {
  return { direct: oids, routed: EMPTY_SET, routedCurrencies: EMPTY_SET };
}

/** Is this owner's share credited under the given selection? */
export function isOwnerCredited(owner: Owner, placement: Placement, sel: Selection): boolean {
  if (sel.direct.has(owner.oid)) return true;
  return sel.routed.has(owner.oid) && sel.routedCurrencies.has(placement.sourceCurrency);
}

function filledOwners(placement: Placement): Owner[] {
  return [
    placement.owners.cro,
    placement.owners.consultant,
    placement.owners.ao,
    placement.owners.conro,
  ].filter((o): o is Owner => o !== null);
}

export function splitForSelection(placement: Placement, sel: Selection): number {
  const filled = filledOwners(placement);
  if (filled.length === 0) return 0;
  const appearances = filled.filter((o) => isOwnerCredited(o, placement, sel)).length;
  return placement.wgp * (appearances / filled.length);
}

/** Does anyone in the selection hold a credited role on this placement? */
export function selectionHasOwner(placement: Placement, sel: Selection): boolean {
  return filledOwners(placement).some((o) => isOwnerCredited(o, placement, sel));
}

/**
 * Placement-count credit — the "Runners by Split" measure, per the splits
 * & territories rules doc §2:
 *
 *   3-way (no CONRO):  CRO 0 · Consultant 0.5 · AO 0.5
 *   4-way (CONRO set): 0.25 each
 *
 * The same person in multiple roles sums (Consultant + AO on a 3-way deal
 * = 1.0). Only credited owners count, under the same direct /
 * currency-routed rules as the fee split; an uncredited role's credit is
 * not redistributed.
 */
export function countCreditForSelection(placement: Placement, sel: Selection): number {
  const { cro, consultant, ao, conro } = placement.owners;
  const roleCredits: Array<[Owner | null, number]> = conro !== null
    ? [[cro, 0.25], [consultant, 0.25], [ao, 0.25], [conro, 0.25]]
    : [[cro, 0], [consultant, 0.5], [ao, 0.5]];
  let total = 0;
  for (const [owner, credit] of roleCredits) {
    if (owner && credit > 0 && isOwnerCredited(owner, placement, sel)) {
      total += credit;
    }
  }
  return total;
}

/**
 * For display purposes: returns the breakdown of how a single person's split
 * was calculated. Used by the drill-down detail panel.
 */
export function explainSplit(placement: Placement, oid: string): {
  filledCount: number;
  appearances: number;
  roles: Array<'CRO' | 'Consultant' | 'AO' | 'CONRO'>;
  value: number;
} {
  const order: Array<['CRO' | 'Consultant' | 'AO' | 'CONRO', Owner | null]> = [
    ['CRO', placement.owners.cro],
    ['Consultant', placement.owners.consultant],
    ['AO', placement.owners.ao],
    ['CONRO', placement.owners.conro],
  ];
  const filled = order.filter(([, o]) => o !== null);
  const roles = filled.filter(([, o]) => o!.oid === oid).map(([r]) => r);
  const value = filled.length
    ? placement.wgp * (roles.length / filled.length)
    : 0;
  return {
    filledCount: filled.length,
    appearances: roles.length,
    roles,
    value,
  };
}
